import {
	ArrowRightIcon,
	ArrowUpRightIcon,
	FlagIcon,
	UserCircleIcon,
	UserPlusIcon,
} from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import type { InvitationListItem } from "@referee-fyi/share";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { EventData } from "robotevents";
import { twMerge } from "tailwind-merge";
import { Button, IconButton, LinkButton } from "~components/Button";
import { Chip, ChipGroup } from "~components/Chip";
import { ClickToCopy, ClickToCopyIcon } from "~components/ClickToCopy";
import { Dialog, DialogBody, DialogHeader } from "~components/Dialog";
import { OfflineNotice } from "~components/DisconnectedWarning";
import { Incident } from "~components/Incident";
import { Checkbox, Input } from "~components/Input";
import { MenuButton } from "~components/MenuButton";
import { Spinner } from "~components/Spinner";
import { toast } from "~components/Toast";
import { UpdatePrompt } from "~components/UpdatePrompt";
import { ErrorMessage, Info, Success, Warning } from "~components/Warning";
import { ReadyState, useShareConnection } from "~models/ShareConnection";
import { tryPersistStorage } from "~utils/data/keyval";
import { queryClient } from "~utils/data/query";
import {
	acceptEventInvitation,
	fetchInvitation,
	forceEventInvitationSync,
	getInstancesForEvent,
	getIntegrationAPIEndpoints,
	getRequestCodeUserKey,
	putRequestCode,
	removeInvitation,
} from "~utils/data/share";
import { WORLDS_EVENTS } from "~utils/data/state";
import { useLocalAssetIdsToUploadForEvent } from "~utils/hooks/assets";
import { useEventIncidents } from "~utils/hooks/incident";
import { useProductFlag } from "~utils/hooks/meta";
import { useEvent } from "~utils/hooks/robotevents";
import {
	useCreateInstance,
	useEventInvitation,
	useForceRegister,
	useIntegrationAPIDeleteIncident,
	useIntegrationAPIIncidents,
	useIntegrationAPIUsers,
	useIntegrationBearer,
	useInviteUser,
	useSystemKeyIntegrationBearer,
} from "~utils/hooks/share";

export type EventSummaryProps = {
	event: EventData;
};

export const EventSummary: React.FC<EventSummaryProps> = ({ event }) => {
	return (
		<section className="bg-zinc-800 p-4 rounded-md mb-4">
			<h2 className="text-xs font-mono text-emerald-400">{event.sku}</h2>
			<h1>{event.name}</h1>
			<div className="mt-2">
				<p>{event.location.venue}</p>
			</div>
		</section>
	);
};

export type ManageDialogProps = {
	open: boolean;
	onClose: () => void;
	sku: string;
};

export const InviteDialog: React.FC<ManageDialogProps> = ({
	open,
	onClose,
	sku,
}) => {
	const [inviteCode, setInviteCode] = useState("");
	const [admin, setAdmin] = useState(false);
	const formId = useId();

	const { data: event } = useEvent(sku);

	const {
		data: response,
		isLoading: isLoadingRequestCode,
		isPending: isGetInvitePending,
	} = useQuery({
		queryKey: ["get_invite", sku, inviteCode],
		queryFn: () => getRequestCodeUserKey(sku, inviteCode),
		enabled: inviteCode.length > 0,
	});

	const user = useMemo(
		() => (response?.success ? response.data.user : null),
		[response],
	);
	const userVersion = useMemo(
		() => (response?.success ? response.data.version : null),
		[response],
	);

	const {
		mutateAsync: invite,
		data: inviteResponse,
		isPending: isInvitePending,
		error: inviteResponseError,
		reset: resetInvite,
	} = useInviteUser(sku, admin);

	const inviteError = useMemo(() => {
		if (inviteResponseError) {
			return inviteResponseError.message;
		}

		if (inviteResponse && !inviteResponse.success) {
			return inviteResponse.error.message;
		}

		return null;
	}, [inviteResponse, inviteResponseError]);

	useEffect(() => {
		if (inviteResponse?.success) {
			setTimeout(() => {
				if (inviteResponse?.success) {
					setInviteCode("");
					setAdmin(false);
					resetInvite();
				}
			}, 2000);
		}
	}, [inviteResponse, resetInvite]);

	return (
		<Dialog open={open} onClose={onClose} mode="modal" aria-label="Invite User">
			<DialogHeader onClose={onClose} title="Invite User" />
			<DialogBody className="px-2">
				<label htmlFor={`${formId}-invite-code`}>
					{event ? <EventSummary event={event} /> : null}
					<p>
						To invite a user to this share instance, enter their invite code.
					</p>
				</label>
				<div className="relative">
					<Input
						id={`${formId}-invite-code`}
						className="w-full font-mono text-6xl text-center mt-4"
						value={inviteCode}
						onChange={(e) => setInviteCode(e.currentTarget.value.toUpperCase())}
					/>
				</div>
				<Spinner show={isLoadingRequestCode} />
				{user ? (
					<div className="mt-4">
						<p>{user.name}</p>
						<ClickToCopy message={user.key} />
						{userVersion !== __REFEREE_FYI_VERSION__ ? (
							<Warning
								message="User is on a different version"
								className="mt-4"
							>
								<p>
									Your app version (<code>{__REFEREE_FYI_VERSION__}</code>) does
									not match this user's app version (
									<code>{userVersion ?? "Unknown"}</code>
									). This can lead to instability.
								</p>
							</Warning>
						) : null}
						<Checkbox
							label="Invite as Admin"
							bind={{ value: admin, onChange: setAdmin }}
						/>

						<Button
							mode="primary"
							className="mt-4"
							onClick={() => invite(user.key)}
						>
							Invite {user.name}
						</Button>
					</div>
				) : null}
				{!user && !isGetInvitePending ? (
					<ErrorMessage message="Invalid Code" className="mt-4" />
				) : null}
				<Spinner show={isInvitePending} className="mt-4" />
				{inviteError ? (
					<ErrorMessage message={inviteError} className="mt-4" />
				) : null}
				{inviteResponse?.success ? (
					<Success message="Sent Invitation!" className="mt-4 bg-emerald-600" />
				) : null}
			</DialogBody>
		</Dialog>
	);
};

export const JoinCodeDialog: React.FC<ManageDialogProps> = ({
	open,
	onClose,
	sku,
}) => {
	const { data: event } = useEvent(sku);

	// Request Code
	const { data: requestCode, isLoading: isLoadingRequestCode } = useQuery({
		queryKey: ["request_code", sku],
		queryFn: () => putRequestCode(sku),
		enabled: open,
		gcTime: 600000,
		refetchInterval: 60000,
	});

	const code = useMemo(() => {
		return requestCode?.success ? requestCode.data.code : "";
	}, [requestCode]);

	// Register user when they open
	const { profile, updateProfile } = useShareConnection([
		"updateProfile",
		"profile",
	]);

	const [localName, setLocalName] = useState("");
	const joinFormId = useId();
	const name = profile?.name;

	useEffect(() => {
		if (!profile) {
			return;
		}
		if (profile.name) {
			setLocalName(profile.name);
		} else {
			setLocalName("");
		}
	}, [profile]);

	useEffect(() => {
		if (!name || !open) {
			return;
		}
		updateProfile({ name });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, name, updateProfile]);

	// Invitation
	const { data: invitation } = useQuery({
		queryKey: ["join_custom_get_invite"],
		queryFn: () => fetchInvitation(sku),
		refetchInterval: 2000,
		networkMode: "always",
		enabled: open,
	});

	const { mutate: setNameContinue, isPending: isPendingSetNameContinue } =
		useMutation({
			mutationFn: () => updateProfile({ name: localName }),
		});

	const onAcceptInvitation = useCallback(async () => {
		if (!invitation || !invitation.success) {
			return;
		}
		await tryPersistStorage();
		await acceptEventInvitation(sku, invitation.data.id);
		onClose();
	}, [invitation, sku, onClose]);

	const onClearInvitation = useCallback(async () => {
		await removeInvitation(sku);
	}, [sku]);

	return (
		<Dialog open={open} onClose={onClose} mode="modal">
			<DialogHeader onClose={onClose} title="Join Request" />
			<DialogBody className="px-2">
				{event ? <EventSummary event={event} /> : null}
				{!profile ? (
					<>
						<p>
							Enter your name to continue. This name will be visible to other
							participants.
						</p>
						<label htmlFor={`${joinFormId}-name`}>
							<h1 className="font-bold mt-4">Name</h1>
						</label>
						<Input
							id={`${joinFormId}-name`}
							className="w-full"
							value={localName}
							onChange={(e) => setLocalName(e.currentTarget.value)}
						/>
						<Button
							className={twMerge("mt-4", !localName ? "opacity-50" : "")}
							mode="primary"
							onClick={() => setNameContinue()}
						>
							Continue
						</Button>
						<Spinner show={isPendingSetNameContinue} />
					</>
				) : null}
				{profile ? (
					<>
						<p className="mb-4">
							To join an existing instance, you will need an admin to invite
							you. Have them enter the code shown below on their device.
						</p>
						<Spinner show={isLoadingRequestCode} />
						<ClickToCopy
							message={code}
							className="font-mono text-6xl text-center"
						/>
						{invitation?.data ? (
							<section className="mt-4">
								<nav className="flex justify-between items-center">
									<Info
										message={`Invitation From ${invitation?.data.from.name}`}
									/>
									{invitation?.data.admin ? (
										<p className="text-sm text-emerald-400">Admin</p>
									) : null}
								</nav>
								<Button
									mode="primary"
									className="mt-2"
									onClick={onAcceptInvitation}
								>
									Accept & Join
								</Button>
								<Button
									mode="dangerous"
									className="mt-2"
									onClick={onClearInvitation}
								>
									Clear Invitation
								</Button>
							</section>
						) : (
							<Spinner className="mt-4" show />
						)}
					</>
				) : null}
			</DialogBody>
		</Dialog>
	);
};

export type LeaveMenuProps = {
	sku: string;
} & React.HTMLProps<HTMLDivElement>;

export const LeaveMenu: React.FC<LeaveMenuProps> = ({ sku, ...props }) => {
	const { data: invitation } = useEventInvitation(sku);

	const { forceSync, disconnect, invitations } = useShareConnection([
		"forceSync",
		"disconnect",
		"invitations",
	]);

	const { mutateAsync: onClickLeave } = useMutation({
		mutationFn: async () => {
			await disconnect();
			await removeInvitation(sku);
			queryClient.invalidateQueries({ queryKey: ["event_invitation_all"] });
			forceSync();
		},
	});

	return (
		<div {...props}>
			<p>
				Are you sure? If you leave, you will need an admin to invite you again.
			</p>
			{invitation?.admin && invitations.filter((i) => i.admin).length < 2 ? (
				<Warning className="mt-4" message="End Session">
					Since you are the last admin, leaving will end this instance and
					remove all other users.
				</Warning>
			) : null}
			<Button mode="dangerous" className="mt-4" onClick={() => onClickLeave()}>
				Leave
			</Button>
			<Button mode="normal" className="mt-4">
				Stay
			</Button>
		</div>
	);
};

export const ProfilePrompt: React.FC = () => {
	const {
		profile: { name },
		updateProfile,
	} = useShareConnection(["profile", "updateProfile"]);

	const [localName, setLocalName] = useState("");
	const profileFormId = useId();
	useEffect(() => {
		if (name) {
			setLocalName(name);
		}
	}, [name]);

	// Save
	const { mutate: setNameContinue, isPending: isPendingSetNameContinue } =
		useMutation({
			mutationFn: () => updateProfile({ name: localName }),
		});

	return (
		<>
			<p>
				Enter your name to continue. This name will be visible to other
				participants.
			</p>
			<label htmlFor={`${profileFormId}-name`}>
				<h1 className="font-bold mt-4">Name</h1>
			</label>
			<Input
				id={`${profileFormId}-name`}
				className="w-full"
				value={localName}
				onChange={(e) => setLocalName(e.currentTarget.value.trim())}
			/>
			<Button
				className={twMerge("mt-4", !localName ? "opacity-50" : "")}
				mode="primary"
				onClick={() => setNameContinue()}
				disabled={!localName}
			>
				Continue
			</Button>
			<Spinner show={isPendingSetNameContinue} />
		</>
	);
};

export const AssetReconciliation: React.FC<ManageTabProps> = ({ event }) => {
	const { data: invitation } = useEventInvitation(event.sku);
	const assetIdsToUpload = useLocalAssetIdsToUploadForEvent(event?.sku);

	if (!invitation?.accepted) {
		return null;
	}

	if (!assetIdsToUpload || assetIdsToUpload.length < 1) {
		return null;
	}

	return (
		<Info message="Upload Images" className="mb-4">
			<p className="mt-2">
				There are {assetIdsToUpload?.length} images on your device that have not
				been uploaded.
			</p>
			<LinkButton
				to="/$sku/assets"
				params={{ sku: event.sku }}
				className="w-full mt-2 flex items-center bg-zinc-900"
			>
				<span className="flex-1">Manage Images</span>
				<ArrowRightIcon height={20} className="text-emerald-400" />
			</LinkButton>
		</Info>
	);
};

export type InstanceUserListItemProps = {
	user: InvitationListItem;
	active: boolean;
};

export const InstanceUserListItem: React.FC<InstanceUserListItemProps> = ({
	user,
	active,
}) => {
	return (
		<ChipGroup className="flex gap-2 items-center flex-1">
			<UserCircleIcon height={24} />
			<p>{user.user.name}</p>
			{user.user.role === "system" ? (
				<Chip className="text-xs bg-blue-700 px-2 py-0.5 rounded-md">
					System
				</Chip>
			) : null}
			{user.admin ? (
				<Chip className="text-xs bg-purple-600 px-2 py-0.5 rounded-md">
					Admin
				</Chip>
			) : null}
			{active ? (
				<Chip className="text-xs bg-emerald-600 px-2 py-0.5 rounded-md">
					Connected
				</Chip>
			) : (
				<Chip className="text-xs bg-zinc-700 px-2 py-0.5 rounded-md">
					Offline
				</Chip>
			)}
		</ChipGroup>
	);
};

export const ShareManager: React.FC<ManageTabProps> = ({ event }) => {
	const productMode = useProductFlag("mode");

	// Dialogs
	const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
	const [joinCodeDialogOpen, setJoinCodeDialogOpen] = useState(false);

	// Instance State
	const { data: invitation } = useEventInvitation(event.sku);
	const connection = useShareConnection([
		"profile",
		"updateProfile",
		"invitations",
		"activeUsers",
		"readyState",
		"forceSync",
		"disconnect",
		"userMetadata",
	]);

	// If we are not in the instance, force an invalidation
	useEffect(() => {
		if (!invitation?.accepted) {
			return;
		}

		const instanceInList = connection.invitations.some(
			(inv) => inv.user.key === connection.profile?.key,
		);

		if (!instanceInList) {
			forceEventInvitationSync(event.sku);
		}
	}, [invitation, connection.invitations, event.sku, connection.profile?.key]);

	const { data: entries } = useEventIncidents(event.sku);
	const isSharing = useMemo(
		() => !!invitation && invitation.accepted,
		[invitation],
	);

	// Remove User
	const { mutateAsync: removeUser, isPending: isPendingRemoveUser } =
		useMutation({
			mutationFn: async (user: string) => {
				await removeInvitation(event.sku, user);
				queryClient.invalidateQueries({ queryKey: ["event_invitation_all"] });
				return connection.forceSync();
			},
		});

	// Begin Sharing
	const { mutateAsync: createInstance } = useCreateInstance(event.sku);
	const {
		mutateAsync: onClickBeginSharing,
		isPending: isCreateInstancePending,
	} = useMutation({
		mutationFn: async () => {
			await tryPersistStorage();
			await connection.updateProfile(connection.profile);
			const response = await createInstance();

			if (response.success) {
				toast({ type: "info", message: "Sharing!" });
			} else {
				toast({
					type: "error",
					message: response.error.message,
					context: JSON.stringify(response),
				});
			}
		},
	});

	if (!connection.profile.name) {
		return (
			<section>
				<h2 className="font-bold">Sharing</h2>
				<ProfilePrompt />
			</section>
		);
	}

	const showSpinner =
		(connection.readyState !== ReadyState.Closed &&
			connection.readyState !== ReadyState.Open) ||
		isPendingRemoveUser ||
		isCreateInstancePending ||
		(invitation?.accepted && connection.readyState !== ReadyState.Open);

	return (
		<section>
			<JoinCodeDialog
				sku={event.sku}
				open={joinCodeDialogOpen}
				onClose={() => setJoinCodeDialogOpen(false)}
			/>
			<InviteDialog
				sku={event.sku}
				open={inviteDialogOpen}
				onClose={() => setInviteDialogOpen(false)}
			/>
			<h2 className="font-bold">Sharing</h2>
			<p>Share Name: {connection.profile.name} </p>
			{isSharing ? (
				<div className="mt-2">
					{invitation?.admin ? (
						<Button
							mode="normal"
							className="flex gap-2 items-center justify-center"
							onClick={() => setInviteDialogOpen(true)}
						>
							<UserPlusIcon height={20} />
							<p>Invite</p>
						</Button>
					) : null}
					<MenuButton
						mode="dangerous"
						className="mt-2"
						menu={<LeaveMenu sku={event.sku} />}
					>
						Leave
					</MenuButton>
					<nav className="flex gap-2 justify-evenly mt-4">
						<p className="text-lg">
							<FlagIcon height={20} className="inline mr-2" />
							<span className="text-zinc-400">
								{entries?.length ?? 0} entries
							</span>
						</p>
						<p className="text-lg">
							<UserCircleIcon height={20} className="inline mr-2" />
							<span className="text-zinc-400">
								{connection.activeUsers.length} active
							</span>
						</p>
					</nav>
					<Spinner show={showSpinner} />
					<ul className="mt-4">
						{connection.invitations.map((user) => (
							<li
								key={user.user.key}
								className="py-2 px-4 rounded-md mt-2 flex"
							>
								<InstanceUserListItem
									user={user}
									active={connection.activeUsers.some(
										(u) => u.key === user.user.key,
									)}
								/>
								{invitation?.admin && !user.admin ? (
									<IconButton
										icon={<TrashIcon height={20} />}
										onClick={() => removeUser(user.user.key)}
										className="bg-transparent"
									/>
								) : null}
							</li>
						))}
					</ul>
				</div>
			) : null}
			{!isSharing ? (
				<section>
					<p>
						Create or join a sharing instance to synchronize the anomaly log
						between devices.
					</p>
					{productMode === "WC" &&
					WORLDS_EVENTS.includes(event.sku) &&
					!connection.userMetadata.isSystemKey ? (
						<p className="mt-2">
							New instances cannot be created for this event. Please reach out
							to your group area supervisor to get access to the existing Worlds
							instances.
						</p>
					) : (
						<Button
							mode="primary"
							className="mt-2"
							disabled={!connection.profile.name}
							onClick={() => onClickBeginSharing()}
						>
							Begin Sharing
						</Button>
					)}
					<Button
						mode="normal"
						className="mt-2"
						onClick={() => setJoinCodeDialogOpen(true)}
					>
						Join Existing
					</Button>
					<Spinner show={showSpinner} />
				</section>
			) : null}
		</section>
	);
};

const EventSummaryLink: React.FC<ManageTabProps> = ({ event }) => {
	return (
		<section className="mt-4">
			<h2 className="font-bold">Event Summary</h2>
			<p>See a summary of all entries at the event.</p>
			<LinkButton
				to="/$sku/summary"
				params={{ sku: event.sku }}
				className="w-full mt-2 flex items-center"
			>
				<span className="flex-1">Event Summary</span>
				<ArrowRightIcon height={20} className="text-emerald-400" />
			</LinkButton>
		</section>
	);
};

const IntegrationInfo: React.FC<ManageTabProps> = ({ event }) => {
	const { data: bearerToken, isSuccess: isSuccessBearerToken } =
		useIntegrationBearer(event.sku);

	const { json, csv, pdf } = useMemo(() => {
		if (!bearerToken) {
			return { json: "", csv: "", pdf: "" };
		}
		return getIntegrationAPIEndpoints(event.sku, { token: bearerToken });
	}, [bearerToken, event.sku]);

	if (!isSuccessBearerToken || !bearerToken) {
		return null;
	}

	return (
		<section>
			<h2 className="mt-4 font-bold">Integrations</h2>
			<p>
				Use these integration URLs to give others up-to-date read-only access to
				the anomaly log for this instance.{" "}
				<em>
					Anyone who has this URL can pull this data at any time, so treat these
					carefully!
				</em>
			</p>
			<ClickToCopy prefix="BEARER" message={bearerToken} className="flex-1" />
			<ClickToCopy prefix="JSON" message={json.toString()} className="flex-1" />
			<ClickToCopy prefix="CSV" message={csv.toString()} className="flex-1" />
			<ClickToCopy prefix="PDF" message={pdf.toString()} className="flex-1" />
		</section>
	);
};

type SystemKeyIntegrationInfoProps = ManageTabProps & {
	instance: string;
};

const SystemKeyIntegrationInfo: React.FC<SystemKeyIntegrationInfoProps> = ({
	event,
	instance,
}) => {
	const { data: bearerToken, isPending: isPendingSystemKeyIntegrationBearer } =
		useSystemKeyIntegrationBearer(event.sku, instance);

	const credentials = {
		token: bearerToken ?? "",
		instance,
	};

	const { mutate: deleteIncident, isPending: isPendingDeleteIncident } =
		useIntegrationAPIDeleteIncident(event.sku, credentials);

	const { json, csv, pdf } = useMemo(() => {
		if (!bearerToken) {
			return { json: "", csv: "", pdf: "" };
		}
		return getIntegrationAPIEndpoints(event.sku, {
			token: bearerToken,
			instance,
		});
	}, [bearerToken, event.sku, instance]);

	const { data: incidents, isPending: isPendingIntegrationAPIIncidents } =
		useIntegrationAPIIncidents(event.sku, credentials, {
			enabled: !!bearerToken,
		});

	const { data: users, isPending: isPendingIntegrationAPIUsers } =
		useIntegrationAPIUsers(event.sku, credentials, { enabled: !!bearerToken });

	const isPending =
		isPendingSystemKeyIntegrationBearer ||
		isPendingIntegrationAPIIncidents ||
		isPendingIntegrationAPIUsers;

	return (
		<div className="mt-4 bg-zinc-900 p-4 rounded-md">
			<h1 className="font-mono flex items-center gap-2 justify-center">
				<span className="text-ellipsis overflow-hidden text-nowrap whitespace-nowrap flex-1">
					{instance}
				</span>
				<ClickToCopyIcon value={instance} className="mt-0" />
			</h1>
			<nav className="flex gap-2 justify-evenly mt-4">
				<p className="text-lg">
					<FlagIcon height={20} className="inline mr-2" />
					<span className="text-zinc-400">
						{incidents?.length ?? 0} entries
					</span>
				</p>
				<p className="text-lg">
					<UserCircleIcon height={20} className="inline mr-2" />
					<span className="text-zinc-400">{users?.active.length} active</span>
				</p>
			</nav>
			<Spinner show={isPending} />
			<ul className="mt-4">
				{users?.invitations.map((user) => (
					<li key={user.user.key} className="py-2 px-4 rounded-md mt-2 flex">
						<InstanceUserListItem
							user={user}
							active={users.active.some((u) => u.key === user.user.key)}
						/>
					</li>
				))}
			</ul>
			<div className="mt-4">
				<ClickToCopy
					message={json.toString()}
					prefix="JSON"
					className="text-center flex items-center gap-4 justify-between"
				>
					JSON
					<ArrowUpRightIcon height={16} className="text-emerald-400" />
				</ClickToCopy>
				<ClickToCopy
					message={csv.toString()}
					prefix="CSV"
					className="text-center flex items-center gap-4 justify-between"
				>
					CSV
					<ArrowUpRightIcon height={16} className="text-emerald-400" />
				</ClickToCopy>
				<ClickToCopy
					message={pdf.toString()}
					prefix="PDF"
					className="text-center flex items-center gap-4 justify-between"
				>
					PDF
					<ArrowUpRightIcon height={16} className="text-emerald-400" />
				</ClickToCopy>
			</div>
			<details className="mt-4 p-2">
				<summary>
					<span className="ml-2">{incidents?.length} Incidents</span>
				</summary>
				<Spinner show={isPendingDeleteIncident} />
				{incidents?.map((incident) => (
					<Incident key={incident.id} incident={incident} readonly>
						<Button
							mode="dangerous"
							onClick={() => deleteIncident(incident.id)}
						>
							Delete
						</Button>
					</Incident>
				))}
			</details>
		</div>
	);
};

type ForceRegisterDialogProps = ManageDialogProps & {
	instances: string[];
};

type BulkEntry = { name: string; key: string };
type BulkResult = { name: string; success: boolean; error?: string };

function parseTSV(tsv: string): BulkEntry[] {
	return tsv
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0)
		.map((line) => {
			const parts = line.split("\t");
			if (parts.length >= 2) {
				return { name: parts[0].trim(), key: parts[1].trim() };
			}
			return null;
		})
		.filter((entry): entry is BulkEntry => entry !== null);
}

type SingleForceRegisterProps = {
	sku: string;
	selectedInstance: string;
	admin: boolean;
};

const SingleForceRegister: React.FC<SingleForceRegisterProps> = ({
	sku,
	selectedInstance,
	admin,
}) => {
	const formId = useId();
	const [key, setKey] = useState("");
	const [name, setName] = useState("");

	const {
		mutateAsync: forceRegister,
		isPending,
		data: result,
		error,
		reset,
	} = useForceRegister(sku);

	const resultError = useMemo(() => {
		if (error) return error.message;
		if (result && !result.success) return result.error.message;
		return null;
	}, [error, result]);

	useEffect(() => {
		if (!result?.success) return;

		const timeoutId = setTimeout(() => {
			setKey("");
			setName("");
			reset();
		}, 2000);

		return () => clearTimeout(timeoutId);
	}, [result, reset]);

	const canSubmit = key.length > 0 && name.length > 0 && selectedInstance;

	return (
		<>
			<label htmlFor={`${formId}-key`}>
				<h2 className="font-bold mt-2">Public Key</h2>
			</label>
			<Input
				id={`${formId}-key`}
				className="w-full font-mono text-sm"
				value={key}
				onChange={(e) => setKey(e.currentTarget.value.trim())}
			/>
			<label htmlFor={`${formId}-name`}>
				<h2 className="font-bold mt-2">Name</h2>
			</label>
			<Input
				id={`${formId}-name`}
				className="w-full"
				value={name}
				onChange={(e) => setName(e.currentTarget.value)}
			/>
			<Button
				mode="primary"
				className={twMerge("mt-4", !canSubmit ? "opacity-50" : "")}
				disabled={!canSubmit}
				onClick={() =>
					forceRegister({
						key,
						name,
						instance: selectedInstance,
						admin,
					})
				}
			>
				Force Register
			</Button>
			<Spinner show={isPending} className="mt-4" />
			{resultError ? (
				<ErrorMessage message={resultError} className="mt-4" />
			) : null}
			{result?.success ? (
				<Success
					message="User registered and added!"
					className="mt-4 bg-emerald-600"
				/>
			) : null}
		</>
	);
};

type BulkForceRegisterProps = {
	sku: string;
	selectedInstance: string;
	admin: boolean;
};

const BulkForceRegister: React.FC<BulkForceRegisterProps> = ({
	sku,
	selectedInstance,
	admin,
}) => {
	const formId = useId();
	const [tsvInput, setTsvInput] = useState("");
	const [bulkResults, setBulkResults] = useState<BulkResult[]>([]);
	const [bulkRunning, setBulkRunning] = useState(false);

	const { mutateAsync: forceRegister } = useForceRegister(sku);

	const bulkEntries = useMemo(() => parseTSV(tsvInput), [tsvInput]);
	const canSubmit = bulkEntries.length > 0 && selectedInstance && !bulkRunning;

	const handleBulkRegister = useCallback(async () => {
		if (!canSubmit) return;
		setBulkRunning(true);
		setBulkResults([]);
		const results: BulkResult[] = [];
		for (const entry of bulkEntries) {
			try {
				const res = await forceRegister({
					key: entry.key,
					name: entry.name,
					instance: selectedInstance,
					admin,
				});
				if (res.success) {
					results.push({ name: entry.name, success: true });
				} else {
					results.push({
						name: entry.name,
						success: false,
						error: res.error?.message ?? "Unknown error",
					});
				}
			} catch (e) {
				results.push({
					name: entry.name,
					success: false,
					error: e instanceof Error ? e.message : "Unknown error",
				});
			}
			setBulkResults([...results]);
		}
		setBulkRunning(false);
	}, [canSubmit, bulkEntries, forceRegister, selectedInstance, admin]);

	return (
		<>
			<label htmlFor={`${formId}-tsv`}>
				<h2 className="font-bold mt-2">Users</h2>
			</label>
			<textarea
				id={`${formId}-tsv`}
				className="w-full bg-zinc-800 text-white rounded-md p-2 font-mono text-xs mt-1 min-h-[120px]"
				value={tsvInput}
				onChange={(e) => setTsvInput(e.currentTarget.value)}
				placeholder={"Alice\t04abc123...\nBob\t04def456..."}
			/>
			<p className="text-zinc-400 mt-1">
				{bulkEntries.length} user{bulkEntries.length !== 1 ? "s" : ""} parsed
			</p>
			<Button
				mode="primary"
				className={twMerge("mt-4", !canSubmit ? "opacity-50" : "")}
				disabled={!canSubmit}
				onClick={handleBulkRegister}
			>
				Register All ({bulkEntries.length})
			</Button>
			<Spinner show={bulkRunning} className="mt-4" />
			{bulkResults.length > 0 ? (
				<ul className="mt-4 text-sm">
					{bulkResults.map((r, i) => (
						<li
							key={`${r.name}-${i}`}
							className={twMerge(
								"py-1 px-2 rounded-md mt-1",
								r.success ? "text-emerald-400" : "text-red-400",
							)}
						>
							{r.name}: {r.success ? "Registered" : r.error}
						</li>
					))}
				</ul>
			) : null}
		</>
	);
};

const ForceRegisterDialog: React.FC<ForceRegisterDialogProps> = ({
	open,
	onClose,
	sku,
	instances,
}) => {
	const formId = useId();
	const [mode, setMode] = useState<"single" | "bulk">("single");
	const [selectedInstance, setSelectedInstance] = useState("");
	const [admin, setAdmin] = useState(false);

	useEffect(() => {
		if (instances.length > 0 && !selectedInstance) {
			setSelectedInstance(instances[0]);
		}
	}, [instances, selectedInstance]);

	const { data: bearerToken } = useSystemKeyIntegrationBearer(
		sku,
		selectedInstance,
	);

	const credentials = useMemo(
		() => ({ token: bearerToken ?? "", instance: selectedInstance }),
		[bearerToken, selectedInstance],
	);

	const { data: users, isPending: isPendingUsers } = useIntegrationAPIUsers(
		sku,
		credentials,
		{ enabled: !!bearerToken && !!selectedInstance },
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			mode="modal"
			aria-label="Force Register User"
		>
			<DialogHeader onClose={onClose} title="Force Register User" />
			<DialogBody className="px-2">
				<div className="flex gap-2 mb-4">
					<Button
						mode={mode === "single" ? "primary" : "normal"}
						onClick={() => setMode("single")}
					>
						Single
					</Button>
					<Button
						mode={mode === "bulk" ? "primary" : "normal"}
						onClick={() => setMode("bulk")}
					>
						Bulk
					</Button>
				</div>
				<label htmlFor={`${formId}-instance`}>
					<h2 className="font-bold mt-2">Instance</h2>
				</label>
				<select
					id={`${formId}-instance`}
					className="w-full bg-zinc-800 text-white rounded-md p-2 font-mono text-sm mt-1"
					value={selectedInstance}
					onChange={(e) => setSelectedInstance(e.currentTarget.value)}
				>
					{instances.map((inst) => (
						<option key={inst} value={inst}>
							{inst}
						</option>
					))}
				</select>
				{selectedInstance ? (
					<div className="mt-2">
						<Spinner show={isPendingUsers} />
						{users?.invitations.length ? (
							<ul>
								{users.invitations.map((user) => (
									<li
										key={user.user.key}
										className="py-1 px-2 rounded-md mt-1 flex"
									>
										<InstanceUserListItem
											user={user}
											active={users.active.some((u) => u.key === user.user.key)}
										/>
									</li>
								))}
							</ul>
						) : !isPendingUsers ? (
							<p className="text-sm">No users in this instance.</p>
						) : null}
					</div>
				) : null}
				<Checkbox
					label="Register as Admin"
					bind={{ value: admin, onChange: setAdmin }}
				/>
				{mode === "single" ? (
					<SingleForceRegister
						sku={sku}
						selectedInstance={selectedInstance}
						admin={admin}
					/>
				) : (
					<BulkForceRegister
						sku={sku}
						selectedInstance={selectedInstance}
						admin={admin}
					/>
				)}
			</DialogBody>
		</Dialog>
	);
};

const SystemKeyInfo: React.FC<ManageTabProps> = ({ event }) => {
	const {
		userMetadata: { isSystemKey },
	} = useShareConnection(["userMetadata"]);

	const [forceRegisterOpen, setForceRegisterOpen] = useState(false);

	const { data: response, isLoading } = useQuery({
		queryKey: ["@referee-fyi", "get_instance_list", event.sku],
		queryFn: () => getInstancesForEvent(event.sku),
		enabled: isSystemKey,
	});

	const instances = useMemo(() => {
		return response?.success ? response.data.instances : [];
	}, [response]);

	if (!isSystemKey) {
		return null;
	}

	if (isLoading) {
		return <Spinner show />;
	}

	return (
		<section>
			<h2 className="mt-4 font-bold">Instance List ({instances.length})</h2>
			<Button
				mode="normal"
				className="mt-2 flex gap-2 items-center justify-center"
				onClick={() => setForceRegisterOpen(true)}
			>
				<UserPlusIcon height={20} />
				<p>Force Register User</p>
			</Button>
			<ForceRegisterDialog
				sku={event.sku}
				open={forceRegisterOpen}
				onClose={() => setForceRegisterOpen(false)}
				instances={instances}
			/>
			{instances.map((instance) => (
				<SystemKeyIntegrationInfo
					key={instance}
					event={event}
					instance={instance}
				/>
			))}
		</section>
	);
};

export type ManageTabProps = {
	event: EventData;
};

export const EventManageTab: React.FC<ManageTabProps> = ({ event }) => {
	return (
		<section className="max-w-xl max-h-full w-full mx-auto flex-1 mb-12 overflow-y-auto">
			<OfflineNotice />
			<UpdatePrompt />
			<AssetReconciliation event={event} />
			<ShareManager event={event} />
			<EventSummaryLink event={event} />
			<IntegrationInfo event={event} />
			<SystemKeyInfo event={event} />
		</section>
	);
};
